<script setup lang="ts">
import { PageEnum } from "@/enums";
import { EventTypeEnum } from "@/models";
import { useGlobalStore, useQuoteStore, useDataLayerStore } from "@/stores";
import CircleProgress from "@/components/general/CircleProgress/CircleProgress.vue";
import PageHeading from "@/components/general/PageHeading/PageHeading.vue";
import RangeField from "@/components/general/RangeField/RangeField.vue";
import Button from "@/components/general/Button/Button.vue";

// Stores
const quote = useQuoteStore();
const dataLayer = useDataLayerStore();
const global = useGlobalStore();

const emit = defineEmits<{
    submitSection: [currentPage: PageEnum, nextPage: PageEnum];
}>();

const submitSection = (currentPage: PageEnum, nextPage: PageEnum) => {
    emit("submitSection", currentPage, nextPage);
};

const payoutChanged = (type: string): void => {
    dataLayer.triggerEvent(
        EventTypeEnum.PageInteraction,
        `Update deal ${type}`
    );
};

defineExpose({ submitSection });
</script>

<template>
    <div :id="PageEnum.SelectCover">
        <PageHeading class="slider-header">
            Move the Slider Below to Select Your Cover
        </PageHeading>
        <PageHeading class="mobile-slider-header">
            Use the Buttons Below to Select Your Cover
        </PageHeading>
        <div class="slider-section-outer">
            <div class="slider-section">
                <div class="slider-section-inner">
                    <div class="cover-value">
                        <div class="monthly-payment">
                            <p>Your monthly premium</p>
                            <p class="large-price"> £{{ quote.myPPM }} </p>
                            <img
                                class="bigger-payout-img"
                                src="@img/180-years-banners.svg"
                                alt="Over 180 years of experience"
                            />
                        </div>
                        <CircleProgress
                            :max="quote.maxSA"
                            :min="0"
                            :value="quote.mySA"
                            aria-label="Your cash sum"
                            aria-labelled-by="circle"
                            class="circle-progress"
                            type="circular"
                            :gauge="6"
                        >
                            <div class="pct">
                                <p>Your cash sum</p>
                                <p class="large-price">
                                    £{{ global.formatNumber(quote.mySA) }}
                                </p>
                            </div>
                        </CircleProgress>
                    </div>
                    <div class="v5-slider-container">
                        <RangeField
                            id="dealSlider"
                            v-model="quote.myDeal"
                            :min="0"
                            :max="quote.availableDeals.length - 1"
                            input-name="cover-slider"
                            aria-labelled-by="cover-slider-label"
                            class="slider"
                            @change="payoutChanged"
                        >
                            <div class="monthly-payment-mobile">
                                <p>Your monthly premium</p>
                                <p class="large-price">£{{ quote.myPPM }}</p>
                            </div>
                        </RangeField>
                    </div>
                </div>
                <Button
                    id="submitCoverLevel"
                    btn-type="primary"
                    class="continue"
                    @click="
                        submitSection(
                            PageEnum.SelectCover,
                            PageEnum.CoverSummary
                        )
                    "
                >
                    Continue
                </Button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.monthly-payment p,
.slider-header {
    display: none;
}

.monthly-payment-mobile,
.mobile-slider-header {
    display: block;
}

.slider-section-outer {
    width: 100vw;
    max-width: 100vw;
    margin-left: -15px;
    padding: 15px;
    background-color: var(--pure-white);
    display: flex;
    justify-content: center;
}

.slider-section-inner {
    width: 100%;
    max-width: 825px;
}

.slider-section {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    width: 100%;
    color: var(--color-primary-dark);
    font-weight: 400;

    .circle-progress {
        width: 321px;
        height: 321px;
        padding: 15px;
    }

    .cover-value {
        display: flex;
        flex-direction: column;
        justify-content: space-around;
        align-items: center;
        margin-bottom: 15px;
        width: 100%;
    }

    p {
        font-size: 1.1rem;
        margin-bottom: 0;

        &.large-price {
            font-size: 3.125rem;
            font-weight: 600;
        }
    }

    .pct,
    .monthly-payment-mobile,
    .monthly-payment {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .monthly-payment-mobile {
        color: var(--pure-white);
    }

    .slider {
        display: flex;
        justify-content: center;
    }
}

@media screen and (width >= 768px) {
    .monthly-payment p,
    .slider-header {
        display: block;
    }

    .monthly-payment-mobile,
    .mobile-slider-header {
        display: none;
    }

    .slider-section {
        padding: 0 15px;

        .cover-value {
            flex-direction: row;
            align-items: baseline;
        }
    }
}

@media screen and (width >= 993px) {
    .slider-section-outer {
        margin-left: calc(-15px - (100vw - 960px) / 2);

        .slider-section {
            max-width: 960px;
        }
    }
}

@media screen and (width >= 1200px) {
    .slider-section-outer {
        margin-left: calc(-15px - (100vw - 1140px) / 2);

        .slider-section {
            max-width: 1140px;
        }
    }
}
</style>
